import { useEffect, useId, useLayoutEffect, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { getFocusable, trapTarget } from './a11y'
import { X } from './icons'

export type DialogProps = {
  open: boolean
  onClose: () => void
  /** Title Case heading. It names the dialog for screen readers. */
  title: ReactNode
  /** Sentence case prose under the title. It describes the dialog for screen readers. */
  description?: ReactNode
  children?: ReactNode
  /** Buttons at the bottom. Put at most one primary button here. */
  footer?: ReactNode
  /** The element that takes focus on open. Without it, `[data-autofocus]` or the first focusable element does. */
  initialFocusRef?: RefObject<HTMLElement | null>
  /** When false, Escape, the backdrop and the close button do nothing (for example while a request runs). */
  dismissible?: boolean
  /** Hides the close icon button. Escape still closes when the dialog is dismissible. */
  hideCloseButton?: boolean
  size?: 'sm' | 'md'
  busy?: boolean
  className?: string
}

/**
 * A modal dialog in a portal. Focus moves in on open, Tab stays inside,
 * Escape closes, and focus returns to the trigger on close.
 */
export function Dialog(props: DialogProps) {
  if (!props.open || typeof document === 'undefined') return null
  return createPortal(<DialogSurface {...props} />, document.body)
}

function DialogSurface({ onClose, title, description, children, footer, initialFocusRef, dismissible = true, hideCloseButton, size = 'md', busy, className = '' }: DialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const pressStartedOnBackdrop = useRef(false)
  const latest = useRef({ onClose, dismissible })
  latest.current = { onClose, dismissible }

  // Move focus in on open. Return it to the trigger when the dialog unmounts.
  useLayoutEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const panel = panelRef.current
    if (panel) {
      // Prefer content over the close button, so a form dialog starts in its first field.
      const focusable = getFocusable(panel)
      const target = initialFocusRef?.current
        ?? panel.querySelector<HTMLElement>('[data-autofocus]')
        ?? focusable.find((element) => !element.classList.contains('ui-dialog__close'))
        ?? focusable[0]
        ?? panel
      target.focus()
    }
    return () => {
      if (trigger && trigger !== document.body && trigger.isConnected) trigger.focus()
    }
    // Runs once per open, because the surface mounts when the dialog opens.
  }, [])

  // Stop the page behind the dialog from scrolling.
  useEffect(() => {
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = overflow }
  }, [])

  function requestClose() {
    if (latest.current.dismissible) latest.current.onClose()
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      event.preventDefault()
      requestClose()
      return
    }
    if (event.key !== 'Tab' || !panelRef.current) return
    const focusable = getFocusable(panelRef.current)
    const current = focusable.indexOf(document.activeElement as HTMLElement)
    const target = trapTarget(current, focusable.length, event.shiftKey)
    if (target === null) return
    event.preventDefault()
    if (target === 'container') panelRef.current.focus()
    else focusable[target].focus()
  }

  return (
    <div
      className="ui-dialog-backdrop"
      onKeyDown={handleKeyDown}
      onMouseDown={(event) => { pressStartedOnBackdrop.current = event.target === event.currentTarget }}
      onClick={(event) => { if (pressStartedOnBackdrop.current && event.target === event.currentTarget) requestClose() }}
    >
      <div
        ref={panelRef}
        className={['ui-dialog', `ui-dialog--${size}`, className].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        aria-busy={busy || undefined}
        tabIndex={-1}
      >
        <div className="ui-dialog__header">
          <h2 id={titleId} className="ui-dialog__title">{title}</h2>
          {!hideCloseButton && (
            <button type="button" className="ui-icon-button ui-dialog__close" aria-label="Close" aria-disabled={!dismissible || undefined} onClick={requestClose}>
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </div>
        {description && <div id={descriptionId} className="ui-dialog__description">{description}</div>}
        {children && <div className="ui-dialog__body">{children}</div>}
        {footer && <div className="ui-dialog__footer">{footer}</div>}
      </div>
    </div>
  )
}
